import * as React from "react";
import { useState, useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import { Formik, Form, Field, useFormikContext } from "formik";
import { Button, Badge, Alert } from "components/bootstrap";
import { Icon, ModalSubmit } from "components/common";
import Popover from "components/common/Popover";
import FieldFiltersConfiguration from "data-lake/FieldFiltersConfiguration/FieldFiltersConfiguration";
import TypeSpecificValue from "views/components/TypeSpecificValue";
import FieldType from "views/logic/fieldtypes/FieldType";
import useFieldTypes from "data-lake/hooks/useFieldTypes";
import OperatorField from "data-lake/preview/OperatorField";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useCurrentDataLakePlugin from "data-lake/preview/hooks/useCurrentDataLakePlugin";
import { DocumentationLink } from "components/support";
import DocsHelper from "util/DocsHelper";
import validateFieldFilters from "data-lake/logic/validateFieldFilters";
import useSelectedDataLake from "data-lake/hooks/useSelectedDataLake";
const Container = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.sm};
  `
);
const QueryString = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${theme.spacings.xs};
    min-height: 34px;
  `
);
const NoFiltersInfo = styled(Alert)`
  margin-top: 0;
`;
const validateQueryBuilderForm = ({
  fieldFilters,
  operator,
  inMemoryFields
}) => {
  const errors = {};
  const fieldFiltersErrors = validateFieldFilters(fieldFilters, operator, inMemoryFields);
  if (fieldFiltersErrors.length > 0) {
    errors.fieldFilters = fieldFiltersErrors;
  }
  return errors;
};
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const SearchBarFieldsFilter = () => {
  const [showBuilder, setShowBuilder] = useState(false);
  const { values: searchBarValues } = useFormikContext();
  const { extraValidations } = useCurrentDataLakePlugin();
  const toggleBuilder = () => setShowBuilder((cur) => !cur);
  const { data: fieldTypes, inMemoryFields } = useFieldTypes();
  const { selectedDataLake } = useSelectedDataLake();
  const sendTelemetry = useSendTelemetry();
  const validation = useCallback(
    ({ fieldFilters, operator }) => validateQueryBuilderForm({ fieldFilters, operator, inMemoryFields }),
    [inMemoryFields]
  );
  const fieldsValidationError = useMemo(() => {
    if (typeof (extraValidations == null ? void 0 : extraValidations.fieldFilters) === "function") return extraValidations.fieldFilters(searchBarValues);
    return null;
  }, [extraValidations, searchBarValues]);
  return /* @__PURE__ */ React.createElement(Field, { name: "fields" }, ({ field: { name, value, onChange } }) => {
    var _a, _b;
    const changeValue = (newValue) => {
      onChange({
        target: {
          value: newValue,
          name
        }
      });
    };
    const onClearFieldFilters = () => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW[selectedDataLake.type].CLEAR_FIELD_FILTERS, {
        app_pathname: "/data-storage/preview",
        app_section: "search-bar",
        app_action_value: "clear-field-filters",
        event_details: {
          data_lake_storage_type: selectedDataLake.storageType
        }
      });
      changeValue({ fieldFilters: [], operator: "AND" });
    };
    const onSubmitBuilder = (newValue) => {
      var _a2;
      sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW[selectedDataLake.type].SUBMIT_FIELD_FILTERS_FORM, {
        app_pathname: "/data-storage/preview",
        app_section: "search-bar",
        app_action_value: "update-field-filters",
        eventDetails: {
          fieldFilters: (_a2 = newValue == null ? void 0 : newValue.fieldFilters) == null ? void 0 : _a2.map(({ field_name: fieldName }) => fieldName),
          operator: newValue == null ? void 0 : newValue.operator,
          data_lake_storage_type: selectedDataLake.storageType
        }
      });
      changeValue(newValue);
      toggleBuilder();
    };
    return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
      Popover,
      {
        position: "bottom-start",
        width: 500,
        opened: showBuilder,
        withArrow: true,
        onChange: setShowBuilder,
        closeOnClickOutside: true,
        withinPortal: true
      },
      /* @__PURE__ */ React.createElement(Popover.Target, null, /* @__PURE__ */ React.createElement(
        Button,
        {
          onClick: toggleBuilder,
          disabled: !!fieldsValidationError,
          title: fieldsValidationError != null ? fieldsValidationError : "Configure field filter"
        },
        "Filter by field ",
        /* @__PURE__ */ React.createElement(Icon, { name: "arrow_drop_down" })
      )),
      ((_a = value == null ? void 0 : value.fieldFilters) == null ? void 0 : _a.length) >= 1 && /* @__PURE__ */ React.createElement(QueryString, null, value.fieldFilters.map(({ field_name: fieldName, value: fieldValue }, index) => (
        // eslint-disable-next-line react/no-array-index-key
        /* @__PURE__ */ React.createElement(React.Fragment, { key: index }, index !== 0 && value.operator, /* @__PURE__ */ React.createElement(Badge, { bsStyle: "info" }, fieldName, ":", /* @__PURE__ */ React.createElement(
          TypeSpecificValue,
          {
            value: fieldValue,
            field: fieldName,
            type: ((fieldTypes == null ? void 0 : fieldTypes.find((f) => f.name === fieldName)) || { type: FieldType.Unknown }).type
          }
        )))
      )), /* @__PURE__ */ React.createElement(Button, { onClick: onClearFieldFilters, title: "Clear field filters" }, "Clear")),
      /* @__PURE__ */ React.createElement(
        Popover.Dropdown,
        {
          title: /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement("span", null, "Build Field Filter"), /* @__PURE__ */ React.createElement(DocumentationLink, { page: DocsHelper.PAGES.DATA_LAKE, displayIcon: true, text: "" })),
          id: "build-fields-filter-dropdown"
        },
        /* @__PURE__ */ React.createElement(
          Formik,
          {
            initialValues: ((_b = value == null ? void 0 : value.fieldFilters) == null ? void 0 : _b.length) > 0 ? value : { fieldFilters: [{ field_name: "", value: "" }], operator: "AND" },
            onSubmit: onSubmitBuilder,
            validate: validation
          },
          ({ isValid, isSubmitting, values }) => {
            var _a2;
            return /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(OperatorField, null), ((_a2 = values == null ? void 0 : values.fieldFilters) == null ? void 0 : _a2.length) < 1 && /* @__PURE__ */ React.createElement(NoFiltersInfo, null, "All field filters have been removed."), /* @__PURE__ */ React.createElement(FieldFiltersConfiguration, null), /* @__PURE__ */ React.createElement(
              ModalSubmit,
              {
                submitButtonText: "Update filter",
                submitLoadingText: "Updateting filter...",
                disabledSubmit: !isValid || isSubmitting,
                onCancel: toggleBuilder
              }
            ));
          }
        )
      )
    ));
  });
};
export default SearchBarFieldsFilter;
